import { WSQueryResult } from "@ws-react-query";
import { useEffect, useMemo, useState } from "react";
import {
  Redirect,
  Route,
  Switch,
  useHistory,
  useLocation
} from "react-router-dom";

import { WSFlexBox, WSLoader } from "@wingspanhq/fe-component-library";

import { RequirementType } from "@wingspanhq/payments/dist/interfaces";
import { useQueryInternalAccounts } from "../../../query/banking/queries/useQueryInternalAccounts";
import { useUserId } from "../../../query/hooks/helpers";
import { useFeatureFlags } from "../../../query/hooks/useFeatureFlags";
import { useQueryAcknowledgement } from "../../../query/onboarding/queries/useQueryAcknowledgement";
import { useQueryCustomerEntity } from "../../../query/onboarding/queries/useQueryCustomerEntity";
import { useQueryVerificationMissingData } from "../../../query/onboarding/queries/useQueryVerificationMissingFields";
import { useQueryVerifications } from "../../../query/onboarding/queries/useQueryVerifications";
import { useAllPayerListQuery } from "../../../query/payers/queries/useAllPayerListQuery";
import {
  usePaymentsStatusQuery,
  usePayoutSettings,
  usePayoutSettingsDebitCards,
  usePayrollSettings
} from "../../../query/payments/queries";
import { useRequirementsListQuery } from "../../../query/requirements/queries/useRequirementsListQuery";
import {
  useAccounts,
  useActivities,
  useClientQuery,
  useMemberProfile
} from "../../../query/users/queries";
import { Layout } from "../../../shared/components/Layout";
import { usePayrollFundingMethodsManagedByOrganization } from "../../../userFeatures/usePayrollFundingMethodsManagedByOrganization";
import { WSServiceError } from "../../../utils/serviceHelper";
import { getAdditionalInformationStepConfig } from "../config/additionalInformation";
import { getBusinessProfileStepConfig } from "../config/businessProfile";
import { getCertifyTaxPayerInfoStepConfig } from "../config/certifyTaxPayerInfo";
import { getCreateAccountStepConfig } from "../config/createAccount";
import { getEligibilityRequirementsStepConfig } from "../config/eligibilityRequirements";
import { getPayoutMethodStepConfig } from "../config/payoutMethod";
import { getPayrollFundingMethodStepConfig } from "../config/payrollFundingMethod";
import { getPayrollSettingsStepConfig } from "../config/payrollSettings";
import { getTaxWithholdingsStepConfig } from "../config/taxWithholdings";
import { Context } from "../Context";
import { OnboardingContext, OnboardingModule, OnboardingStep } from "../types";
import { StepResult } from "./StepResult";

type Props = {
  basePath: string;
  modules: OnboardingModule[];
  onBack?: () => void;
  onSuccess?: () => void;
  isLoading?: boolean;
};

const loadStep = (
  step: OnboardingStep | null,
  ...dependencies: WSQueryResult<any, WSServiceError>[]
): OnboardingStep | null => {
  if (step === null) return null;

  if (dependencies.every(query => query.isFetched)) {
    return {
      ...step
    };
  }

  return {
    ...step,
    component: () => (
      <WSFlexBox.Center py="3XL">
        <WSLoader.Spinner size="M" />
      </WSFlexBox.Center>
    ),
    status: "Loading"
  };
};

export const OnboardingFlow: React.FC<Props> = ({
  basePath,
  modules,
  onBack,
  onSuccess,
  isLoading
}) => {
  const userId = useUserId();
  const location = useLocation();

  usePaymentsStatusQuery();
  const queryVerifications = useQueryVerifications();
  const queryPayrollSettings = usePayrollSettings(userId);
  const queryPayoutSettings = usePayoutSettings(userId);
  const queryAccounts = useAccounts();
  const queryActivity = useActivities(userId);
  const queryAllPayerList = useAllPayerListQuery();
  const queryClient = useClientQuery(userId);
  const queryInternalAccounts = useQueryInternalAccounts();
  const queryDebitCards = usePayoutSettingsDebitCards(userId);
  const queryMissingDataTax = useQueryVerificationMissingData("Tax");
  const queryMissingDataBanking = useQueryVerificationMissingData("Banking");
  const queryMissingDataEnhanced = useQueryVerificationMissingData("Enhanced");
  const queryMonitoredEntity = useQueryCustomerEntity();
  const queryMember = useMemberProfile(userId);
  const queryFeatureFlags = useFeatureFlags();

  const isPayrollFundingMethodManagedByOrg =
    usePayrollFundingMethodsManagedByOrganization();

  const queryRequirements = useRequirementsListQuery({
    filter: {
      payeeId: userId,
      payerPayeeEngagementIds: {
        "!=": "null"
      },
      requirementType: {
        notIn: [
          RequirementType.Registration,
          RequirementType.PayoutMethod,
          RequirementType.TaxVerification
        ]
      }
    },
    sort: {
      updatedAt: "desc"
    }
  });

  const queryElectronicTaxFormConsentAcknowledgement = useQueryAcknowledgement(
    "ElectronicTaxFormConsent"
  );

  const [contextValue, setContextValue] = useState<OnboardingContext>({});

  const steps = useMemo(
    () =>
      modules
        .map(onboardingModule => {
          switch (onboardingModule.type) {
            case "create_account":
              return loadStep(
                getCreateAccountStepConfig(
                  onboardingModule,
                  contextValue,
                  queryVerifications.data,
                  queryMissingDataTax.data,
                  queryMissingDataBanking.data
                ),
                queryVerifications,
                queryMissingDataTax,
                queryMissingDataBanking
              );

            case "payroll_funding_method":
              return loadStep(
                getPayrollFundingMethodStepConfig(
                  onboardingModule,
                  contextValue,
                  queryPayrollSettings.data,
                  queryAccounts.data,
                  queryActivity.data,
                  queryClient.data,
                  isPayrollFundingMethodManagedByOrg
                ),
                queryPayrollSettings,
                queryAccounts,
                queryActivity,
                queryClient
              );

            case "payroll_settings":
              return loadStep(
                getPayrollSettingsStepConfig(
                  onboardingModule,
                  contextValue,
                  queryActivity.data
                ),
                queryActivity
              );
            case "additional_information":
              return getAdditionalInformationStepConfig(
                onboardingModule,
                contextValue,
                queryFeatureFlags.data,
                queryMissingDataEnhanced.data,
                queryVerifications.data
              );

            case "business_profile":
              return loadStep(
                getBusinessProfileStepConfig(
                  onboardingModule,
                  contextValue,
                  queryActivity.data
                ),
                queryActivity
              );
            case "certify_tax_payer_info":
              return loadStep(
                getCertifyTaxPayerInfoStepConfig(
                  onboardingModule,
                  contextValue,
                  queryAllPayerList.data,
                  queryElectronicTaxFormConsentAcknowledgement.data
                ),
                queryAllPayerList,
                queryElectronicTaxFormConsentAcknowledgement
              );
            case "payout_method":
              return loadStep(
                getPayoutMethodStepConfig(
                  onboardingModule,
                  contextValue,
                  queryPayoutSettings.data,
                  queryAccounts.data,
                  queryDebitCards.data,
                  queryInternalAccounts.data,
                  queryMissingDataBanking.data,
                  queryFeatureFlags.data
                ),
                queryPayoutSettings,
                queryAccounts,
                queryDebitCards,
                queryInternalAccounts
              );
            case "eligibility_requirements":
              return loadStep(
                getEligibilityRequirementsStepConfig(
                  onboardingModule,
                  contextValue,
                  queryRequirements.data
                ),
                queryRequirements
              );

            case "tax_withholdings":
              return loadStep(
                getTaxWithholdingsStepConfig(
                  onboardingModule,
                  contextValue,
                  queryMonitoredEntity.data,
                  queryMember.data,
                  queryInternalAccounts.data,
                  queryMissingDataBanking.data,
                  queryFeatureFlags.data
                ),
                queryMonitoredEntity,
                queryMember,
                queryInternalAccounts
              );

            default:
              throw new Error(
                `No step config found for module type: ${
                  (onboardingModule as OnboardingModule).type
                }`
              );
          }
        })
        .filter(step => step !== null) as OnboardingStep[],
    [
      modules,
      contextValue,
      queryVerifications,
      queryMissingDataTax,
      queryMissingDataBanking,
      queryMissingDataEnhanced,
      queryPayrollSettings,
      queryAccounts,
      queryActivity,
      queryClient,
      isPayrollFundingMethodManagedByOrg,
      queryAllPayerList,
      queryElectronicTaxFormConsentAcknowledgement,
      queryPayoutSettings,
      queryDebitCards,
      queryInternalAccounts,
      queryRequirements,
      queryMonitoredEntity,
      queryMember
    ]
  );

  const currentStep = useMemo(() => {
    return steps.find(step => location.pathname.includes(step.slug));
  }, [location.pathname, steps]);

  if (!steps.length || isLoading) {
    return null;
  }

  return (
    <Context.Provider
      value={{
        value: contextValue,
        setValue: setContextValue,
        modules
      }}
    >
      <Layout
        title={currentStep?.title}
        navigation={steps.length > 1 ? steps : undefined}
        onClose={onBack}
      >
        <FlowSwitch
          path={basePath}
          slug={basePath}
          title="Onboarding"
          steps={steps}
          onBack={onBack}
          onNext={onSuccess}
        />
      </Layout>
    </Context.Provider>
  );
};

type StepProps = { path: string } & OnboardingStep;

export const FlowSwitch: React.FC<StepProps> = props => {
  const history = useHistory();
  const location = useLocation();
  const state = location.state;

  const handleLastStepOnNext = useMemo(() => {
    if (!!props.result?.Complete || !!props.result?.Pending) {
      // Do nothing on last step if parent can have Complete or Pending states
      return;
    }

    return props.onNext;
  }, [props.onNext, props.result]);

  const stepsWithProps: OnboardingStep[] = useMemo(() => {
    const steps = props.steps || [];
    return steps.map((step, index) => {
      return {
        ...step,
        onBack:
          index === 0
            ? props.onBack
            : () => {
                history.push(`${props.path}/${steps[index - 1].slug}`, state);
              },
        onSkip: handleLastStepOnNext,
        onNext:
          index === steps.length - 1
            ? handleLastStepOnNext
            : () => {
                history.push(`${props.path}/${steps[index + 1].slug}`, state);
              }
      };
    });
  }, [
    handleLastStepOnNext,
    history,
    props.onBack,
    props.path,
    props.steps,
    state
  ]);

  return (
    <Switch>
      {(!props.status || props.status === "None") &&
        stepsWithProps.map(step => {
          const path = `${props.path}/${step.slug}`;

          return (
            <Route key={step.slug} path={path}>
              <FlowSwitch {...step} path={path} />
            </Route>
          );
        })}

      <Route path={props.path + "/complete"}>
        {props.result?.Complete ? (
          <StepResult
            status="Complete"
            onNext={props.onNext}
            {...props.result?.Complete}
          />
        ) : (
          <GoNext onNext={props.onNext} />
        )}
      </Route>

      <Route path={props.path + "/pending"}>
        {props.result?.Pending ? (
          <StepResult
            status="Pending"
            onNext={props.onNext}
            {...props.result?.Pending}
          />
        ) : (
          <GoNext onNext={props.onNext} />
        )}
      </Route>

      <Route path={props.path}>
        {props.status === "Complete" ? (
          <Redirect to={{ pathname: props.path + "/complete", state }} />
        ) : props.status === "Pending" ? (
          <Redirect to={{ pathname: props.path + "/pending", state }} />
        ) : props.component ? (
          <props.component
            {...props}
            onSkip={props.onNext}
            onNext={handleLastStepOnNext}
          />
        ) : (
          <Redirect
            to={{ pathname: props.path + "/" + stepsWithProps[0].slug, state }}
          />
        )}
      </Route>
    </Switch>
  );
};

const GoNext: React.FC<{ onNext?: () => void }> = props => {
  useEffect(() => {
    props.onNext?.();
  }, [props]);

  return null;
};
